import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Stack, Tabs } from '@mantine/core';
import { IconClipboardList, IconGauge, IconRuler2 } from '@tabler/icons-react';
import { useTenantFeatures } from '../../lib/tenantFeatures';

/** Owned by G22 (plan.md S0). Layout route for the Silo & Counts screen group:
 * silo readings/reconciliation (§8.2), stock counts (§8.6), and silo
 * calibration config (this goal's sole ownership — QC fix #3). Sub-navigation
 * mirrors the Shell's own NavLink pattern (src/components/Shell.tsx) at a
 * smaller scale, scoped to this screen group's own routes.
 *
 * Tab order and default landing: counts-first ruling, 2026-08-24 — Howick has
 * no silos, so landing operators on an empty silo page is wrong. Stock counts
 * is the first tab and the section's default landing (silo-counts/index.tsx
 * redirects there); the silo tabs stay, for Sabie. */
export const Route = createFileRoute('/_authenticated/silo-counts')({
  component: SiloCountsLayout,
});

// Exported so nav.test.ts can assert NAV_SECTIONS' "Silo & Counts" children mirror
// this verbatim (label + to + order) rather than drifting silently.
export const TABS = [
  {
    to: '/silo-counts/counts',
    label: 'Stock counts',
    icon: IconClipboardList,
    match: (path: string) => path.startsWith('/silo-counts/counts'),
  },
  {
    to: '/silo-counts/status',
    label: 'Silo status',
    icon: IconGauge,
    match: (path: string) => path === '/silo-counts/status' || path === '/silo-counts/new-reading',
  },
  {
    to: '/silo-counts/calibration',
    label: 'Calibration',
    icon: IconRuler2,
    match: (path: string) => path === '/silo-counts/calibration',
  },
] as const;

function SiloCountsLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { silosEnabled } = useTenantFeatures();
  // Fallback (unmatched pathname, e.g. the section index mid-redirect) is the
  // counts-first landing tab, not the old silo-status default.
  const active = TABS.find((t) => t.match(pathname))?.to ?? '/silo-counts/counts';

  // Silos off (Stuart ruling 2026-08-26, default for every tenant incl.
  // Howick): the status/calibration/new-reading routes redirect away from
  // themselves (see each route's own `beforeLoad`), so Stock counts is the
  // only page this layout ever renders — a tab row with nothing to switch
  // between is just noise. TABS itself stays exported and untouched (contract
  // with nav.test.ts's parity check).
  if (!silosEnabled) {
    return (
      <Stack gap="lg">
        <Outlet />
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Tabs value={active}>
        <Tabs.List>
          {TABS.map((tab) => (
            <Tabs.Tab
              key={tab.to}
              value={tab.to}
              leftSection={<tab.icon size={16} />}
              // `Tabs.Tab`'s `component` prop is typed `any` (unlike `NavLink`'s true
              // polymorphic factory Shell.tsx uses), so extra props like `to` aren't
              // type-checked through it — `renderRoot` is Mantine's supported escape
              // hatch for exactly this: it receives the tab's fully-assembled DOM/ARIA
              // props (including `children`) to render onto an arbitrary root element.
              renderRoot={(props) => <Link to={tab.to} {...props} />}
            >
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>
      <Outlet />
    </Stack>
  );
}
